import { comparators, dataTypes } from '@adalo/constants'
import {
  getMostSuitableDateFromInput,
  isBetween,
  isBetweenDateOnly,
  isBefore,
  isBeforeDateOnly,
  isAfter,
  isAfterDateOnly,
  isSame,
  isSameDateOnly,
} from 'utils/luxon'
import { asKilometers, kilometersBetweenLocations } from './location'

export const empty = itm => {
  return [null, undefined, ''].includes(itm)
}

/**
 *
 * @param {object | string | number} value
 * @param {import('types').Comparator} comparator
 * @param {import('types').DataType} dataType
 * @param {*} comparison
 * @param {*} [comparison2]
 * @param {*} [comparatorOptions]
 * @returns
 */
export const evaluateCondition = (
  value,
  comparator,
  dataType,
  comparison,
  comparison2,
  comparatorOptions = {}
) => {
  const equality = [comparators.EQUAL, comparators.NOT_EQUAL].includes(
    comparator
  )

  if (value === undefined && !equality) {
    return false
  }

  if ([comparators.TRUE, comparators.FALSE].includes(comparator)) {
    dataType = dataTypes.BOOLEAN
  }

  // Date or Date-only
  if (dataType === dataTypes.DATE || dataType === dataTypes.DATE_ONLY) {
    if (!empty(value)) {
      if (dataType === dataTypes.DATE_ONLY) {
        value = value.slice(0, 10)
      }
      value = getMostSuitableDateFromInput(value)
    }
    if (!empty(comparison)) {
      if (dataType === dataTypes.DATE_ONLY) {
        comparison = comparison.slice(0, 10)
      }
      comparison = getMostSuitableDateFromInput(comparison)
    }
    if (!empty(comparison2)) {
      if (dataType === dataTypes.DATE_ONLY) {
        comparison2 = comparison2.slice(0, 10)
      }
      comparison2 = getMostSuitableDateFromInput(comparison2)
    }

    if (!comparatorOptions?.isVisibility) {
      if (empty(comparison)) {
        if (equality) {
          return empty(value)
        }

        return !comparison
      }

      if (empty(comparison2) && comparator === comparators.BETWEEN) {
        return !comparison2
      }
    }
  }

  // Text
  if (dataType === dataTypes.TEXT) {
    if (!empty(value)) {
      value = String(value)
    }
    if (!empty(comparison)) {
      comparison = String(comparison)
    }
    if (!empty(comparison2)) {
      comparison2 = String(comparison2)
    }
  }

  // Numbers
  if (dataType === dataTypes.NUMBER) {
    if (typeof value === 'string') {
      value = value.trim() === '' ? undefined : Number(value)
    }

    if (typeof comparison === 'string') {
      comparison = comparison.trim() === '' ? undefined : Number(comparison)
    }

    if (typeof comparison2 === 'string') {
      comparison2 = comparison2.trim() === '' ? undefined : Number(comparison2)
    }
  }

  const isZero = dataType === dataTypes.NUMBER && value === 0

  switch (comparator) {
    case comparators.EQUAL:
      if (empty(comparison) && !isZero) {
        return !value
      }

      if (dataType === dataTypes.DATE_ONLY) {
        return isSameDateOnly(value, comparison)
      }

      if (dataType === dataTypes.DATE) {
        return isSame(value, comparison)
      }

      return value === comparison
    case comparators.NOT_EQUAL:
      if (empty(comparison) && !isZero) {
        return !!value
      }

      return value !== comparison
    case comparators.GREATER_THAN:
      if (dataType === dataTypes.DATE_ONLY) {
        return isAfterDateOnly(value, comparison)
      }

      if (dataType === dataTypes.DATE) {
        return isAfter(value, comparison)
      }

      return value > comparison
    case comparators.LESS_THAN:
      if (dataType === dataTypes.DATE_ONLY) {
        return isBeforeDateOnly(value, comparison)
      }

      if (dataType === dataTypes.DATE) {
        return isBefore(value, comparison)
      }

      return value < comparison
    case comparators.GREATER_THAN_OR_EQUAL:
      return value >= comparison
    case comparators.LESS_THAN_OR_EQUAL:
      return value <= comparison
    case comparators.BETWEEN:
      if (dataType === dataTypes.DATE_ONLY) {
        return isBetweenDateOnly(value, comparison, comparison2)
      }

      if (dataType === dataTypes.DATE) {
        return isBetween(value, comparison, comparison2)
      }

      return value >= comparison && value < comparison2
    case comparators.DISTANCE_LESS_THAN: {
      if (
        empty(comparison) ||
        empty(comparatorOptions) ||
        empty(comparatorOptions.radius) ||
        empty(comparatorOptions.unit) ||
        typeof comparison !== 'object'
      ) {
        return true
      }

      if (empty(value)) {
        // Comparing distance between anything and an empty value is always false.
        return false
      }

      const radiusKms = asKilometers(
        comparatorOptions.radius,
        comparatorOptions.unit
      )
      const result =
        kilometersBetweenLocations(value.coordinates, comparison.coordinates) <
        radiusKms
      return result
    }
    case comparators.DISTANCE_GREATER_THAN: {
      if (
        empty(comparison) ||
        empty(comparatorOptions) ||
        empty(comparatorOptions.radius) ||
        empty(comparatorOptions.unit) ||
        typeof comparison !== 'object'
      ) {
        return true
      }

      if (empty(value)) {
        // Comparing distance between anything and an empty value is always false.
        return false
      }

      const radiusKms = asKilometers(
        comparatorOptions.radius,
        comparatorOptions.unit
      )
      const result =
        kilometersBetweenLocations(value.coordinates, comparison.coordinates) >
        radiusKms
      return result
    }
    case comparators.TRUE:
      return !!value
    case comparators.FALSE:
      return !value
    case comparators.EMPTY:
      return !value
    case comparators.NOT_EMPTY:
      return !!value
    case comparators.CONTAINS:
      return (
        String(value)
          .toLowerCase()
          .indexOf((comparison || '').toLowerCase().trim()) !== -1
      )
    case comparators.LIST_CONTAINS: {
      const listIds = (value || []).map(itm => itm.id || itm)
      const comparisonId = (comparison && comparison.id) || comparison

      return listIds.includes(comparisonId)
    }
    case comparators.LIST_NOT_CONTAINS: {
      const listIds = (value || []).map(itm => itm.id || itm)
      const comparisonId = (comparison && comparison.id) || comparison

      return !listIds.includes(comparisonId)
    }
  }

  return false
}
